import * as Sentry from '@sentry/react';
import type { InfiniteData, Query } from '@tanstack/react-query';
import { COLLECTION_SCORING_WINDOW_MS, isImageUrlPending, pollWhilePending, queryKeys } from '@vizit/api';
import { useSearchCollectionImagesInfiniteQuery, useSearchImagesInfiniteQuery } from '@vizit/api';
import type { CollectionImageView, ImageView, SearchCollectionImagesQuery, SearchImagesQuery } from '@vizit/api';
import type { SearchCollectionImagesInfiniteQueryVariables, SearchImagesInfiniteQueryVariables } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS, SPARK_IMAGE_TYPE_CONFIG } from '@vizit/constants';
import * as React from 'react';
import type { CustomPair } from '../../hooks/use-custom-pair-score';
import type { SparkImage } from './spark-images-grid';
import { isAwaitingPairScore, isProcessedUnderPair } from './spark-images-grid.utils';
import { isProcessedImage, rawComparisonScore } from './spark-images-grid.utils';

const SPARK_IMAGES_PAGE_SIZE = 12;

interface SparkImagesQueryKeyProps {
  workspaceId: string;
  imageId: string;
  imageType?: ImageView['image_type'];
  /** The collection's selected pair, a retailer's included: its listing scores the images under it */
  customPair?: CustomPair | null;
  limit?: number;
}

/** The children of `imageId` from the collection listing, scored under the pair and sorted by that score. */
function sparkPairImagesVariables(
  props: SparkImagesQueryKeyProps & { customPair: CustomPair },
): SearchCollectionImagesInfiniteQueryVariables {
  const { workspaceId, imageId, customPair, limit = SPARK_IMAGES_PAGE_SIZE } = props;
  return {
    workspace_id: workspaceId,
    collection_id: customPair.collectionId,
    limit,
    sort_by: 'pair_score',
    sort_direction: 'desc',
    parent_id: [imageId],
    source: ['generated'],
    exclude_source: [],
    product_category_id: customPair.productCategoryId,
    benchmark_id: customPair.benchmarkId ?? undefined,
    retailer: customPair.retailer ?? undefined,
  };
}

/** The children of `imageId` from the workspace listing, sorted by their own score. */
function sparkImagesVariables(props: SparkImagesQueryKeyProps): SearchImagesInfiniteQueryVariables {
  const { workspaceId, imageId, imageType, limit = SPARK_IMAGES_PAGE_SIZE } = props;
  return {
    workspace_id: workspaceId,
    limit,
    sort_by: SPARK_IMAGE_TYPE_CONFIG[imageType ?? 'carousel'].sortBy,
    sort_direction: 'desc',
    parent_id: [imageId],
    source: ['generated'],
    exclude_source: [],
  };
}

/** The key of the query the Spark Images grid reads, for the writes and refreshes the generation flow makes to it. */
function getSparkImagesQueryKey(props: SparkImagesQueryKeyProps): readonly unknown[] {
  const { customPair } = props;
  return customPair
    ? useSearchCollectionImagesInfiniteQuery.getKey(sparkPairImagesVariables({ ...props, customPair }))
    : queryKeys.images.infinite(sparkImagesVariables(props)).queryKey;
}

type SparkPairImagesQuery = Query<
  SearchCollectionImagesQuery,
  unknown,
  InfiniteData<SearchCollectionImagesQuery, unknown>,
  readonly unknown[]
>;

/**
 * Re-reads the children while any is still landing: its resized URL is not served yet, or the pair has yet to score
 * it. It stands in for the listing's own poll, which watches the image's own score instead of the pair's. A child the
 * pair is scoring is watched from the later of the pair's request and its own timestamp, as it may be long served.
 */
function sparkPairImagesRefetchInterval(customPair: CustomPair) {
  return pollWhilePending<SparkPairImagesQuery>(
    (query) =>
      (query.state.data?.pages ?? [])
        .flatMap((page) => page.data)
        .filter((image) => isImageUrlPending(image) || isAwaitingPairScore(image, customPair.requestedAt))
        .map((image) => {
          const own = image.updated_at ?? image.created_at ?? null;
          // A pair scoring an old child has not touched the child, so its own timestamps are stale; a child newer
          // than the pair's request keeps its own, which is the later of the two.
          const isPairNewer = !own || Date.parse(customPair.requestedAt) > Date.parse(own);
          return image.pair_score?.scoring_status === 'pending' && isPairNewer ? customPair.requestedAt : own;
        }),
    {
      staleTimeoutMs: COLLECTION_SCORING_WINDOW_MS,
      onTimeout: (pendingCount) => {
        Sentry.captureException(new Error('Spark images still landing under the pair after the scoring window.'), {
          level: 'warning',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Collection,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Collection].collectionAssetPolling,
          },
          extra: { ...customPair, pending_count: pendingCount },
        });
      },
    },
  );
}

function toSparkImage(imageView: ImageView): SparkImage {
  return {
    id: imageView.id,
    url: imageView.url,
    name: imageView.name || 'Generated image',
    score: imageView.display_score ?? null,
    rawScore: rawComparisonScore(imageView),
    isOriginal: false,
    isLoading: !isProcessedImage(imageView),
    omitScore: !!imageView.omit_score,
    favorite: !!imageView.favorite,
    workspace_id: imageView.workspace_id,
    mostRecentImageGenBatchId: null,
    thumbnailUrl760: imageView.url_760,
    thumbnailUrl360: imageView.url_360,
    thumbnailUrl64: imageView.url_64,
  };
}

/** A child as the pair scores it: the pair's score is shown, and its unrounded one is what the hide toggle compares. */
function toSparkImageUnderPair(imageView: CollectionImageView, customPair: CustomPair, readAt: number): SparkImage {
  const pairScore = imageView.pair_score?.scored ? imageView.pair_score : null;
  return {
    ...toSparkImage(imageView as ImageView),
    score: pairScore?.vizit_score ?? null,
    rawScore: pairScore?.raw_score ?? null,
    isLoading: !isProcessedUnderPair(imageView, customPair.requestedAt, readAt),
  };
}

interface UseSparkImagesProps {
  /**
   * The ID of the portfolio to fetch spark images for
   */
  workspaceId: string;
  /**
   * The ID of the image to fetch spark images for
   */
  imageId: string;
  /**
   * Number of items to fetch per page
   * @default 12
   */
  limit?: number;
  /**
   * Whether the hook should be enabled
   * @default true
   */
  enabled?: boolean;
  /**
   * Whether image generation is currently in progress
   * When true, 7 skeleton items will be shown at the start
   */
  isImageGenerationInProgress?: boolean;
  /**
   * The parent image's type. Controls which score field the API sorts by
   * (hero_score for hero, vizit_score otherwise), so the rendered order matches
   * the score the sort was based on.
   */
  imageType?: ImageView['image_type'];
  /**
   * The collection's selected pair, a retailer's included. The grid then lists the children from the collection,
   * scored and sorted under the pair, so they show the same score as the original and the details header.
   */
  customPair?: CustomPair | null;
  /**
   * The collection has not said yet which pair it selected. Neither listing is read until it does, so the grid never
   * shows, or empties out of, the wrong one.
   */
  isCustomPairLoading?: boolean;
}

interface UseSparkImagesResult {
  /**
   * Array of all spark images from all fetched pages
   */
  images: SparkImage[];
  /**
   * Whether the initial data is loading
   */
  isLoading: boolean;
  /**
   * Whether there was an error fetching data
   */
  hasError: boolean;
  /**
   * Whether there are more pages to fetch
   */
  hasNextPage: boolean;
  /**
   * Whether the next page is currently being fetched
   */
  isFetchingNextPage: boolean;
  /**
   * Function to load the next page of data
   */
  fetchNextPage: () => Promise<unknown>;
  /**
   * Total count of spark images
   */
  totalCount: number;
  /**
   * Function to refetch the spark images data
   */
  refetch: () => Promise<unknown>;
  /**
   * Function to generate more spark images
   */
  generateMore?: () => Promise<void>;
  /**
   * Whether image generation is in progress
   */
  isGenerating?: boolean;
  /**
   * Whether image generation failed
   */
  hasGenerationError?: boolean;
}

/**
 * Hook to fetch spark images with infinite loading and generation capabilities
 *
 * Features:
 * - Infinite loading with pagination
 * - Image generation with loading states
 * - Cache manipulation for loading skeletons
 * - Background polling for score/URL updates
 * - Automatic data transformation to SparkImage format
 *
 * @param props - The hook configuration
 * @returns Object containing spark images data and control functions
 *
 * @example
 * ```tsx
 * const {
 *   images,
 *   isLoading,
 *   hasNextPage,
 *   fetchNextPage,
 *   generateMore,
 *   isGenerating
 * } = useSparkImages({
 *   workspaceId: 'portfolio-123',
 *   imageId: 'image-456',
 *   limit: 12
 * });
 * ```
 */
function useSparkImages(props: UseSparkImagesProps): UseSparkImagesResult {
  const {
    workspaceId,
    imageId,
    limit = SPARK_IMAGES_PAGE_SIZE,
    isImageGenerationInProgress = false,
    enabled = false,
    imageType,
    customPair,
    isCustomPairLoading = false,
  } = props;

  const canRead = enabled && !isCustomPairLoading;
  const workspaceQuery = useSearchImagesInfiniteQuery(
    sparkImagesVariables({ workspaceId, imageId, imageType, limit }),
    {
      enabled: canRead && !customPair,
    },
  );
  const pairQuery = useSearchCollectionImagesInfiniteQuery(
    customPair
      ? sparkPairImagesVariables({ workspaceId, imageId, customPair, limit })
      : { workspace_id: workspaceId, collection_id: '' },
    {
      enabled: canRead && !!customPair,
      refetchInterval: customPair ? sparkPairImagesRefetchInterval(customPair) : false,
    },
  );
  const { data, isLoading, error, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } = customPair
    ? pairQuery
    : workspaceQuery;
  // A refetch that reads the same children keeps the same data, so the read time is what moves a child past its wait.
  const pairReadAt = pairQuery.dataUpdatedAt;

  const images = React.useMemo(() => {
    const pages = (data as { pages?: Array<SearchImagesQuery | SearchCollectionImagesQuery> } | undefined)?.pages;
    const allImages = pages?.flatMap((page) => page.data) || [];
    const sparkImages = allImages.map((imageView) =>
      customPair
        ? toSparkImageUnderPair(imageView as CollectionImageView, customPair, pairReadAt)
        : toSparkImage(imageView as ImageView),
    );

    if (isImageGenerationInProgress) {
      const skeletonItems = Array.from(
        { length: 7 },
        (_, index): SparkImage => ({
          id: `skeleton-${index}`,
          url: null,
          name: 'Generating...',
          score: null,
          isOriginal: false,
          isLoading: true,
          omitScore: false,
          mostRecentImageGenBatchId: null,
        }),
      );
      return [...skeletonItems, ...sparkImages];
    }

    return sparkImages;
  }, [data, isImageGenerationInProgress, customPair, pairReadAt]);

  const totalCount = React.useMemo(() => {
    const infiniteData = data as { pages?: SearchImagesQuery[] } | undefined;
    return infiniteData?.pages?.[0]?.total_count ?? 0;
  }, [data]);

  return {
    images,
    isLoading,
    hasError: !!error,
    hasNextPage: hasNextPage || false,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
    totalCount,
  };
}

export { getSparkImagesQueryKey, useSparkImages };
export type { UseSparkImagesProps, UseSparkImagesResult };
